import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faChevronLeft, faChevronRight, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { IMGS, WA } from '../../constants';
import { ExternalLink, Icon, SectionTitle } from '../ui';

const Gallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const totalSlides = IMGS.length;

  // Auto-play
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 4000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);

  const handleTouchEnd = () => {
    const diff = touchStart - touchEnd;
    if (diff > 75) nextSlide();
    else if (diff < -75) prevSlide();
  };

  return (
    <section id="galeria" className="py-12 sm:py-20 lg:py-28 bg-gradient-to-b from-goshine-dark to-goshine-navy/30 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <SectionTitle 
          badge="Nossos Trabalhos"
          badgeIcon={faImages}
          title="Resultados que"
          highlight="impressionam"
        />

        {/* Carousel */}
        <div 
          className="relative max-w-4xl mx-auto"
          onTouchStart={e => setTouchStart(e.targetTouches[0].clientX)}
          onTouchMove={e => setTouchEnd(e.targetTouches[0].clientX)}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden glass-effect"
            style={{ height: 'clamp(500px, 75vh, 700px)' }}
          >
            {IMGS.map((img, index) => (
              <div 
                key={index} 
                className={`absolute inset-0 transition-opacity duration-500 ease-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                style={{ willChange: index === currentSlide ? 'opacity' : 'auto' }}
              >
                <img 
                  src={img.src} 
                  alt={img.alt} 
                  className="w-full h-full object-cover" 
                  loading={index === 0 ? 'eager' : 'lazy'} 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-goshine-dark/60 via-transparent to-transparent" />
              </div>
            ))}

            {/* Desktop Navigation Arrows */}
            <button 
              onClick={prevSlide} 
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 rounded-full items-center justify-center text-white hover:bg-goshine-orange transition-colors" 
              aria-label="Anterior"
            >
              <Icon icon={faChevronLeft} />
            </button>
            <button 
              onClick={nextSlide} 
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 rounded-full items-center justify-center text-white hover:bg-goshine-orange transition-colors" 
              aria-label="Próximo"
            >
              <Icon icon={faChevronRight} />
            </button>

            {/* Mobile Swipe Hint */}
            <div className="sm:hidden absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs flex items-center gap-2">
              <Icon icon={faChevronLeft} className="text-[10px]" />
              <span>Deslize para ver mais</span>
              <Icon icon={faChevronRight} className="text-[10px]" />
            </div>
          </div>

          {/* Dots Pagination */}
          <div className="flex justify-center gap-2 mt-4 sm:mt-6">
            {IMGS.map((_, index) => (
              <button 
                key={index} 
                onClick={() => setCurrentSlide(index)} 
                className={`h-2 rounded-full transition-[width,background-color] duration-300 ${index === currentSlide ? 'w-8 bg-goshine-orange' : 'w-2 bg-white/30 hover:bg-white/50'}`} 
                aria-label={`Slide ${index + 1}`} 
              />
            ))}
          </div>

          {/* Desktop Thumbnails */}
          <div className="hidden md:flex justify-center gap-3 mt-6">
            {IMGS.map((img, index) => (
              <button 
                key={index} 
                onClick={() => setCurrentSlide(index)} 
                className={`w-20 h-14 rounded-lg overflow-hidden transition-opacity duration-300 ${index === currentSlide ? 'ring-2 ring-goshine-orange opacity-100' : 'opacity-50 hover:opacity-80'}`}
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8 sm:mt-12">
          <ExternalLink href={WA} className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base">
            Quero esse resultado
            <FontAwesomeIcon icon={faArrowRight} />
          </ExternalLink>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
